// 产品矩阵系统 - 画布网格组件
// v2.5.0 - 支持已放置产品的二次拖拽
import React, { useRef, useState, useMemo } from 'react';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { Image as ImageIcon, X, GripVertical } from 'lucide-react';
import { useCanvasStore } from '../stores/canvas-store';
import { 
  useResizeObserver, 
  useProductCache,
} from '../hooks/useOptimizedCanvas';
import { LAYOUT_CONFIGS, CUSTOM_LAYOUT_CONFIG, CANVAS_CONFIG } from '../constants';
import type { CanvasCell, StyleType, LayoutType, Product } from '../types';

interface CanvasGridProps {
  isDarkMode?: boolean;
}

export function CanvasGrid({ isDarkMode = false }: CanvasGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [editingLabel, setEditingLabel] = useState<{ type: 'row' | 'col'; index: number } | null>(null);
  
  // 使用优化的 ResizeObserver Hook
  const containerSize = useResizeObserver(containerRef);
  
  // 使用产品缓存 Hook - O(1) 查找
  const { getProductById } = useProductCache();
  
  const { 
    layoutType, 
    styleType, 
    canvasData, 
    selectedCellIndex, 
    selectCell, 
    removeProductFromCell,
    columnLabels,
    rowLabels,
    showAxisLabels,
    showBorder,
    showCardBackground,
    setColumnLabel,
    setRowLabel,
    customRows,
    customCols,
    cardContentSettings,
    // 合并相关
    isMergeMode,
    selectedCells,
    toggleCellSelection,
    unmergeCells,
  } = useCanvasStore();

  // 使用 useMemo 缓存布局配置计算
  const config = useMemo(() => {
    if (layoutType === 'grid_custom') {
      return { 
        rows: customRows, 
        cols: customCols, 
        gapRatio: CUSTOM_LAYOUT_CONFIG.defaultGapRatio 
      };
    }
    return LAYOUT_CONFIGS[layoutType] || LAYOUT_CONFIGS.grid_3x3;
  }, [layoutType, customRows, customCols]);
  
  // 使用 useMemo 缓存网格尺寸计算
  const gridCalculation = useMemo(() => {
    const size = Math.min(
      containerSize.width || 600, 
      containerSize.height || 600, 
      CANVAS_CONFIG.maxSize
    );
    const labelSize = showAxisLabels ? Math.max(24, size * 0.04) : 0;
    const gridSize = size - labelSize;
    const dynamicGapRatio = config.gapRatio || Math.max(0.005, 0.03 / Math.max(config.rows, config.cols));
    const gapSize = Math.round(gridSize * dynamicGapRatio);
    const totalGapWidth = gapSize * (config.cols - 1);
    const totalGapHeight = gapSize * (config.rows - 1);
    const cellWidth = Math.floor((gridSize - totalGapWidth) / config.cols);
    const cellHeight = Math.floor((gridSize - totalGapHeight) / config.rows);
    const cellSize = Math.min(cellWidth, cellHeight);
    
    return { labelSize, gridSize, gapSize, cellSize };
  }, [containerSize.width, containerSize.height, showAxisLabels, config]);
  
  const { labelSize, gapSize, cellSize } = gridCalculation;
  
  // 使用 useMemo 缓存单元格与产品的映射（包含型号信息）
  const cellsWithProducts = useMemo(() => {
    return canvasData.cells.map(cell => {
      const product = cell.product_id ? getProductById(cell.product_id) : undefined;
      // 如果有 model_id，从产品的 models 中找到对应的型号
      const selectedModel = cell.model_id && product?.models
        ? product.models.find(m => m.id === cell.model_id)
        : undefined;
      return {
        ...cell,
        product,
        selectedModel,
      };
    });
  }, [canvasData.cells, getProductById]);
  
  // 使用 useMemo 缓存 CSS Grid 样式
  const gridStyle = useMemo(() => ({
    gridTemplateColumns: `repeat(${config.cols}, ${cellSize}px)`,
    gridTemplateRows: `repeat(${config.rows}, ${cellSize}px)`,
    gap: `${gapSize}px`,
  }), [config.cols, config.rows, cellSize, gapSize]);

  const handleLabelEdit = (type: 'row' | 'col', index: number, value: string) => {
    if (type === 'col') {
      setColumnLabel(index, value);
    } else {
      setRowLabel(index, value);
    }
    setEditingLabel(null);
  };

  return (
    <div 
      ref={containerRef}
      className="w-full aspect-square flex items-center justify-center"
      style={{ maxWidth: `${CANVAS_CONFIG.maxSize}px`, margin: '0 auto' }}
    >
      <div className="flex flex-col" data-canvas-export>
        {/* 顶部列标题（X轴） */}
        {showAxisLabels && (
          <div className="flex" style={{ marginLeft: labelSize }}>
            {columnLabels.slice(0, config.cols).map((label, index) => (
              <div
                key={`col-${index}`}
                className="flex items-center justify-center"
                style={{ 
                  width: cellSize, 
                  height: labelSize,
                  marginRight: index < config.cols - 1 ? gapSize : 0,
                }}
              >
                {editingLabel?.type === 'col' && editingLabel?.index === index ? (
                  <input
                    type="text"
                    defaultValue={label}
                    autoFocus
                    className="w-full text-center text-xs bg-transparent border-b border-blue-500 outline-none"
                    onBlur={(e) => handleLabelEdit('col', index, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleLabelEdit('col', index, (e.target as HTMLInputElement).value);
                      }
                    }}
                  />
                ) : (
                  <span
                    className={`text-xs font-medium cursor-pointer hover:text-blue-600 truncate px-1 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}
                    onClick={() => setEditingLabel({ type: 'col', index })}
                    title="点击编辑"
                  >
                    {label}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex">
          {/* 左侧行标题（Y轴） */}
          {showAxisLabels && (
            <div className="flex flex-col" style={{ width: labelSize }}>
              {rowLabels.slice(0, config.rows).map((label, index) => (
                <div
                  key={`row-${index}`}
                  className="flex items-center justify-center"
                  style={{ 
                    height: cellSize,
                    marginBottom: index < config.rows - 1 ? gapSize : 0,
                  }}
                >
                  {editingLabel?.type === 'row' && editingLabel?.index === index ? (
                    <input
                      type="text"
                      defaultValue={label}
                      autoFocus
                      className="w-full text-center text-xs bg-transparent border-b border-blue-500 outline-none"
                      style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                      onBlur={(e) => handleLabelEdit('row', index, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleLabelEdit('row', index, (e.target as HTMLInputElement).value);
                        }
                      }}
                    />
                  ) : (
                    <span
                      className={`text-xs font-medium cursor-pointer hover:text-blue-600 truncate ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}
                      style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                      onClick={() => setEditingLabel({ type: 'row', index })}
                      title="点击编辑"
                    >
                      {label}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* 网格区域 - 使用缓存的样式 */}
          <div className="grid" style={gridStyle}>
            {/* 使用预缓存的产品数据，过滤隐藏的单元格 */}
            {cellsWithProducts
              .filter(cell => !cell.hidden)
              .map((cellWithProduct) => (
              <MemoizedCanvasCell
                key={cellWithProduct.index}
                cell={cellWithProduct}
                index={cellWithProduct.index}
                cellSize={cellSize}
                gapSize={gapSize}
                styleType={styleType}
                isDarkMode={isDarkMode}
                isSelected={selectedCellIndex === cellWithProduct.index}
                isMultiSelected={selectedCells.includes(cellWithProduct.index)}
                isMergeMode={isMergeMode}
                product={cellWithProduct.product}
                selectedModel={cellWithProduct.selectedModel}
                onSelect={() => isMergeMode ? toggleCellSelection(cellWithProduct.index) : selectCell(cellWithProduct.index)}
                onRemove={() => removeProductFromCell(cellWithProduct.index)}
                onUnmerge={() => unmergeCells(cellWithProduct.index)}
                layoutType={layoutType}
                showBorder={showBorder}
                showCardBackground={showCardBackground}
                cardContentSettings={cardContentSettings}
                row={cellWithProduct.row}
                col={cellWithProduct.col}
                rowSpan={cellWithProduct.rowSpan}
                colSpan={cellWithProduct.colSpan}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// 单元格组件属性类型
interface CanvasCellComponentProps {
  cell: CanvasCell & { product?: Product };
  index: number;
  cellSize: number;
  gapSize: number;
  styleType: StyleType;
  isDarkMode: boolean;
  isSelected: boolean;
  isMultiSelected?: boolean;
  isMergeMode?: boolean;
  product?: Product;
  selectedModel?: import('../types').ProductModel;
  onSelect: () => void;
  onRemove: () => void;
  onUnmerge?: () => void;
  layoutType: LayoutType;
  showBorder: boolean;
  showCardBackground: boolean;
  row: number;
  col: number;
  rowSpan?: number;
  colSpan?: number;
  cardContentSettings: {
    showTitle: boolean;
    showModelRange: boolean;
    showSlogan: boolean;
    showFeatures: boolean;
    featuresCount: number;
    titleColor: string;
    sloganColor: string;
    featureColor: string;
    textPaddingLeft: number;
  };
}

// 单个单元格组件 - 支持拖放和已放置产品的二次拖拽
function CanvasCellComponent({
  cell,
  index,
  cellSize,
  gapSize,
  styleType,
  isDarkMode,
  isSelected,
  isMultiSelected,
  isMergeMode,
  product,
  selectedModel,
  onSelect,
  onRemove,
  onUnmerge,
  layoutType,
  showBorder,
  showCardBackground,
  cardContentSettings,
  row,
  col,
  rowSpan = 1,
  colSpan = 1,
}: CanvasCellComponentProps) {
  // Droppable - 接收产品放入
  const { isOver, setNodeRef: setDroppableRef } = useDroppable({
    id: `cell-${index}`,
    data: {
      type: 'cell',
      index,
    },
  });

  // Draggable - 已放置的产品可以拖出
  const { 
    attributes, 
    listeners, 
    setNodeRef: setDraggableRef, 
    transform, 
    isDragging 
  } = useDraggable({
    id: `placed-product-${index}`,
    data: {
      type: 'placed-product',
      sourceIndex: index,
      product_id: cell.product_id,
      model_id: cell.model_id,
    },
    disabled: !product, // 只有有产品时才能拖拽
  });

  const isEmpty = !product;
  const isCompact = layoutType === 'grid_9x9';

  // 拖拽时的变换样式
  const dragStyle = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: 100,
  } : undefined;

  // 是否是合并的单元格
  const isMerged = rowSpan > 1 || colSpan > 1;
  
  // 计算合并后的实际尺寸（包含间隙）
  const mergedWidth = cellSize * colSpan + gapSize * (colSpan - 1);
  const mergedHeight = cellSize * rowSpan + gapSize * (rowSpan - 1);
  
  // 选中状态的边框样式（仅用于 overlay）
  const hasSelectionRing = isSelected || isMultiSelected || (isOver && !isDragging);

  return (
    <div
      ref={setDroppableRef}
      onClick={onSelect}
      className={`
        relative overflow-hidden cursor-pointer
        ${styleType === 'minimal' ? 'rounded-none' : 'rounded-lg'}
        ${isMergeMode && isEmpty && !isMultiSelected ? 'hover:bg-green-500/5 transition-all duration-200' : ''}
        ${isDragging ? 'opacity-40' : ''}
        ${isEmpty
          ? `${showBorder ? 'border-2 border-dashed' : ''} ${
              isDarkMode 
                ? `${showBorder ? 'border-gray-600' : ''} ${showCardBackground ? 'bg-gray-700/50' : ''}` 
                : `${showBorder ? 'border-gray-300' : ''} ${showCardBackground ? 'bg-gray-50' : ''}`
            }`
          : `${showBorder ? (isDarkMode ? '' : 'border border-gray-200') : ''} ${
              showCardBackground 
                ? (isDarkMode ? 'bg-gray-700' : 'bg-white') 
                : ''
            }`
        }
        transition-all duration-150
      `}
      style={{ 
        width: isMerged ? mergedWidth : cellSize, 
        height: isMerged ? mergedHeight : cellSize,
        // 使用明确的网格位置定位（CSS Grid 从 1 开始索引）
        gridColumnStart: col + 1,
        gridColumnEnd: col + 1 + colSpan,
        gridRowStart: row + 1,
        gridRowEnd: row + 1 + rowSpan,
      }}
    >
      {/* 选中状态指示器 - 导出时隐藏 */}
      {hasSelectionRing && (
        <div 
          data-export-hide
          className={`
            absolute inset-0 pointer-events-none rounded-lg z-20
            ${isOver && !isDragging ? 'ring-2 ring-blue-500 ring-offset-2' : ''}
            ${isSelected && !isMultiSelected ? 'ring-2 ring-blue-600' : ''}
            ${isMultiSelected ? 'ring-2 ring-green-500 ring-offset-1 animate-pulse bg-green-500/10' : ''}
          `}
        />
      )}
      {/* 合并模式下的悬停提示 - 导出时隐藏 */}
      {isMergeMode && isEmpty && !isMultiSelected && (
        <div 
          data-export-hide
          className="absolute inset-0 pointer-events-none rounded-lg z-10 opacity-0 hover:opacity-100 ring-2 ring-green-400 transition-opacity duration-200"
        />
      )}
      {isEmpty ? (
        <div className="w-full h-full flex flex-col items-center justify-center" data-export-hide>
          <div className={`rounded-lg ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}
            style={{ padding: Math.max(4, cellSize * 0.05) }}
          >
            <ImageIcon 
              style={{ width: Math.max(12, cellSize * 0.15), height: Math.max(12, cellSize * 0.15) }}
              className={isDarkMode ? 'text-gray-400' : 'text-gray-400'}
            />
          </div>
          {!isCompact && (
            <span 
              className={`mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}
              style={{ fontSize: Math.max(10, cellSize * 0.06) }}
            >
              Drop Product Here
            </span>
          )}
        </div>
      ) : (
        // 可拖拽的产品卡片容器
        <div 
          ref={setDraggableRef}
          {...listeners}
          {...attributes}
          className="w-full h-full"
          style={dragStyle}
        >
          <ProductCellContent
            product={product}
            selectedModel={selectedModel}
            styleType={styleType}
            cellSize={cellSize}
            isDarkMode={isDarkMode}
            onRemove={onRemove}
            layoutType={layoutType}
            isDragging={isDragging}
            showCardBackground={showCardBackground}
            cardContentSettings={cardContentSettings}
            rowSpan={rowSpan}
            colSpan={colSpan}
            gapSize={gapSize}
          />
        </div>
      )}
    </div>
  );
}

// 使用 React.memo 优化单元格重渲染
const MemoizedCanvasCell = React.memo(CanvasCellComponent, (prevProps, nextProps) => {
  // 使用版本号或关键属性快速判断是否需要重渲染
  return (
    prevProps.cell.product_id === nextProps.cell.product_id &&
    prevProps.cell.model_id === nextProps.cell.model_id &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isMultiSelected === nextProps.isMultiSelected &&
    prevProps.isMergeMode === nextProps.isMergeMode &&
    prevProps.cellSize === nextProps.cellSize &&
    prevProps.gapSize === nextProps.gapSize &&
    prevProps.styleType === nextProps.styleType &&
    prevProps.isDarkMode === nextProps.isDarkMode &&
    prevProps.layoutType === nextProps.layoutType &&
    prevProps.showBorder === nextProps.showBorder &&
    prevProps.showCardBackground === nextProps.showCardBackground &&
    prevProps.cardContentSettings === nextProps.cardContentSettings &&
    prevProps.row === nextProps.row &&
    prevProps.col === nextProps.col &&
    prevProps.rowSpan === nextProps.rowSpan &&
    prevProps.colSpan === nextProps.colSpan
  );
});

// 产品内容渲染组件 - 根据不同风格渲染
function ProductCellContent({
  product,
  selectedModel,
  styleType,
  cellSize,
  isDarkMode,
  onRemove,
  layoutType,
  isDragging = false,
  showCardBackground = true,
  cardContentSettings,
  rowSpan = 1,
  colSpan = 1,
  gapSize = 0,
}: {
  product: Product;
  selectedModel?: import('../types').ProductModel;
  styleType: StyleType;
  cellSize: number;
  isDarkMode: boolean;
  onRemove: () => void;
  layoutType: LayoutType;
  isDragging?: boolean;
  showCardBackground?: boolean;
  cardContentSettings: {
    showTitle: boolean;
    showModelRange: boolean;
    showSlogan: boolean;
    showFeatures: boolean;
    featuresCount: number;
    titleColor: string;
    sloganColor: string;
    featureColor: string;
    textPaddingLeft: number;
  };
  rowSpan?: number;
  colSpan?: number;
  gapSize?: number;
}) {
  const isCompact = layoutType === 'grid_9x9';
  const isMedium = layoutType === 'grid_4x4';
  const isLarge = layoutType === 'grid_2x2' || layoutType === 'grid_3x3';
  
  // 计算合并后的实际尺寸
  const isMerged = rowSpan > 1 || colSpan > 1;
  const mergedWidth = cellSize * colSpan + gapSize * (colSpan - 1);
  const mergedHeight = cellSize * rowSpan + gapSize * (rowSpan - 1);
  
  // 使用合并后的尺寸计算文字大小（取短边作为参考，确保文字比例合适）
  const effectiveSize = isMerged 
    ? Math.min(mergedWidth, mergedHeight) 
    : cellSize;
  
  // 合并单元格时使用更大的字体缩放因子
  const mergeScale = isMerged ? Math.sqrt(Math.max(rowSpan, colSpan)) : 1;
  
  const titleSize = Math.max(10, Math.min(28, effectiveSize * 0.08 * mergeScale));
  const subtitleSize = Math.max(8, Math.min(20, effectiveSize * 0.06 * mergeScale));
  const smallTextSize = Math.max(8, Math.min(16, effectiveSize * 0.05 * mergeScale));
  const padding = Math.max(4, effectiveSize * 0.03);

  // 删除按钮组件
  const RemoveButton = () => (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onRemove();
      }}
      className="absolute top-1 right-1 p-1 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
    >
      <X className="w-3 h-3 text-white" />
    </button>
  );

  // ========== 1. 标准布局 (standard) ==========
  // 参考洲明风格：标题(名称+型号) + 场景slogan + 带圆点特点列表
  if (styleType === 'standard') {
    const imageHeight = isCompact ? 70 : isMedium ? 60 : 50;
    
    // 生成型号范围文字：优先使用具体型号，否则使用产品间距范围
    // 如果拖入的是具体型号（如 P1.2），显示该型号
    // 如果拖入的是整个产品，显示间距范围（如 1.2/2.5）
    const modelRangeText = selectedModel 
      ? `(${selectedModel.model_name})` 
      : (product.pitch_range 
          ? `(${product.pitch_range.replace(/P/g, '').replace(/-/g, '/')})` 
          : '');
    
    // 获取特点列表（优先使用 features，否则用 process_type 和 series_name 生成）
    const featuresList = product.features?.length 
      ? product.features 
      : [
          product.process_type && `${product.process_type}`,
          product.brightness && `Brightness: ${product.brightness}`,
          product.refresh_rate && `Refresh: ${product.refresh_rate}`,
        ].filter(Boolean) as string[];
    
    // 使用 cardContentSettings 控制显示
    const { showTitle, showModelRange, showSlogan, showFeatures, featuresCount, sloganColor, featureColor, textPaddingLeft } = cardContentSettings;
    
    return (
      <div className="w-full h-full flex flex-col items-center group relative overflow-hidden">
        {/* 图片区域 - 设置固定宽度 */}
        <div 
          className={`relative overflow-hidden flex items-center justify-center ${showCardBackground ? 'bg-gray-900' : ''}`}
          style={{ height: `${imageHeight}%`, width: '100%' }}
        >
          {product.image_url ? (
            <img 
              src={product.image_url} 
              alt={product.name} 
              className="object-contain"
              style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto' }}
            />
          ) : (
            <div className={`w-full h-full flex items-center justify-center ${showCardBackground ? 'bg-gradient-to-br from-gray-800 to-gray-900' : ''}`}>
              <ImageIcon style={{ width: cellSize * 0.15, height: cellSize * 0.15 }} className="text-gray-600" />
            </div>
          )}
        </div>
        
        {/* 文字区域 - 与图片区域同宽，内容居中后左对齐 */}
        <div 
          className="flex-1 w-full flex flex-col items-center justify-start overflow-hidden" 
          style={{ padding }}
        >
          {/* 内容容器 - 固定宽度居中，内部左对齐 */}
          <div 
            className="text-left"
            style={{ 
              width: '100%',
              maxWidth: `${100 - textPaddingLeft * 2}%`,
            }}
          >
            {/* 标题行：产品名称 + 型号范围 */}
            {showTitle && (
              <h4 className={`font-bold leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                style={{ fontSize: titleSize }}>
                {product.name}
                {!isCompact && showModelRange && modelRangeText && (
                  <span className="font-normal opacity-70" style={{ fontSize: Math.max(8, titleSize * 0.7) }}>
                    {modelRangeText}
                  </span>
                )}
              </h4>
            )}
            
            {/* 场景/Slogan - 可自定义颜色 */}
            {showSlogan && (!isCompact || isMerged) && (product.slogan || product.series_name) && (
              <p className="truncate" 
                style={{ 
                  fontSize: subtitleSize, 
                  marginTop: Math.max(2, effectiveSize * 0.01),
                  color: sloganColor || '#22c55e'
                }}>
                {product.slogan || product.series_name}
              </p>
            )}
            
            {/* 特点列表 - 带小圆点，数量可控，合并单元格也显示 */}
            {showFeatures && (isLarge || isMerged) && featuresList.length > 0 && (
              <div className="overflow-hidden" style={{ marginTop: Math.max(4, effectiveSize * 0.02) }}>
                {featuresList.slice(0, featuresCount).map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-1" style={{ marginTop: idx > 0 ? Math.max(2, effectiveSize * 0.005) : 0 }}>
                    <span className="flex-shrink-0" style={{ fontSize: smallTextSize, lineHeight: 1.4, color: featureColor || '#22c55e' }}>•</span>
                    <span className={`truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                      style={{ fontSize: smallTextSize, lineHeight: 1.4 }}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <RemoveButton />
      </div>
    );
  }

  // ========== 2. 左右分割 (split) ==========
  if (styleType === 'split') {
    return (
      <div className="w-full h-full flex group relative">
        <div className={`w-1/2 h-full overflow-hidden flex items-center justify-center ${showCardBackground ? 'bg-gray-900' : ''}`}>
          {product.image_url ? (
            <img src={product.image_url} alt={product.name} className="w-full h-full object-contain" />
          ) : (
            <ImageIcon style={{ width: effectiveSize * 0.15, height: effectiveSize * 0.15 }} className="text-gray-600" />
          )}
        </div>
        <div className="w-1/2 flex flex-col justify-center overflow-hidden" style={{ padding }}>
          <h4 className={`font-semibold leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
            style={{ fontSize: titleSize, lineHeight: 1.2 }}>{product.name}</h4>
          {product.series_name && (
            <p className="text-green-600" style={{ fontSize: subtitleSize, marginTop: 2 }}>{product.series_name}</p>
          )}
          {(!isCompact || isMerged) && product.pitch_range && (
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
              style={{ fontSize: smallTextSize, marginTop: 4 }}>{product.pitch_range}</p>
          )}
          {(isLarge || isMerged) && product.process_type && (
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
              style={{ fontSize: smallTextSize }}>{product.process_type}</p>
          )}
        </div>
        <RemoveButton />
      </div>
    );
  }

  // ========== 3. 对角线动感 (diagonal) ==========
  if (styleType === 'diagonal') {
    // 计算透明模式下图片的最大尺寸（留出边距和文字区域）
    const maxImageWidth = effectiveSize * 0.9;  // 90% 宽度
    const maxImageHeight = effectiveSize * 0.7; // 75% 高度（给文字留空间）
    
    return (
      <div className="w-full h-full group relative overflow-hidden">
        {/* 斜切背景 */}
        {showCardBackground && (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-500 opacity-90"
            style={{ clipPath: 'polygon(0 0, 100% 0, 100% 30%, 0 70%)' }} />
        )}
        
        {/* 图片区域 */}
        <div 
          className={`absolute overflow-hidden flex items-center justify-center ${showCardBackground ? 'bg-gray-900' : ''}`}
          style={{ 
            top: showCardBackground ? 0 : '5%',
            left: showCardBackground ? 0 : '5%',
            right: showCardBackground ? 0 : '5%',
            bottom: showCardBackground ? 0 : '15%',
            clipPath: showCardBackground ? 'polygon(0 30%, 100% 0, 100% 100%, 0 100%)' : 'none',
            paddingTop: showCardBackground ? '15%' : 0,
          }}
        >
          {product.image_url ? (
            <img 
              src={product.image_url} 
              alt={product.name} 
              style={{ 
                maxWidth: showCardBackground ? '100%' : maxImageWidth,
                maxHeight: showCardBackground ? '100%' : maxImageHeight,
                width: 'auto',
                height: 'auto',
                objectFit: 'contain'
              }}
            />
          ) : (
            <ImageIcon style={{ width: effectiveSize * 0.2, height: effectiveSize * 0.2 }} className="text-gray-600" />
          )}
        </div>
        
        {/* 文字区域 - 左上角 */}
        <div className="absolute top-0 left-0 p-2 z-10" style={{ maxWidth: '70%' }}>
          <h4 className={`font-bold truncate drop-shadow-lg ${showCardBackground ? 'text-white' : (isDarkMode ? 'text-white' : 'text-gray-900')}`} style={{ fontSize: titleSize }}>
            {product.name}
          </h4>
          {(!isCompact || isMerged) && product.pitch_range && (
            <p className={`truncate drop-shadow ${showCardBackground ? 'text-white/80' : (isDarkMode ? 'text-gray-400' : 'text-gray-500')}`} style={{ fontSize: smallTextSize }}>
              {product.pitch_range}
            </p>
          )}
        </div>
        
        <RemoveButton />
      </div>
    );
  }

  // ========== 4. 杂志风格 (magazine) ==========
  if (styleType === 'magazine') {
    const accentColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];
    const colorIndex = product.name?.charCodeAt(0) % accentColors.length || 0;
    const accentColor = accentColors[colorIndex];
    
    return (
      <div className={`w-full h-full group relative ${showCardBackground ? 'bg-white' : ''}`}>
        {/* 左侧彩色边条 */}
        {showCardBackground && (
          <div className="absolute left-0 top-0 bottom-0" 
            style={{ width: Math.max(3, effectiveSize * 0.02), backgroundColor: accentColor }} />
        )}
        
        {/* 顶部彩色标签 */}
        {showCardBackground && (
          <div className="absolute top-2 left-3 px-2 py-0.5 rounded-sm z-10"
            style={{ backgroundColor: accentColor }}>
            <span className="text-white font-medium" style={{ fontSize: Math.max(8, smallTextSize - 1) }}>
              {product.category === 'all_in_one' ? 'ALL-IN-ONE' : product.category?.toUpperCase() || 'PRODUCT'}
            </span>
          </div>
        )}
        
        {/* 图片区域 */}
        <div className={`absolute top-0 right-0 overflow-hidden ${showCardBackground ? 'bg-gray-900' : ''}`}
          style={{ width: showCardBackground ? '65%' : '100%', height: showCardBackground ? '60%' : '70%' }}>
          {product.image_url ? (
            <img src={product.image_url} alt={product.name} className="w-full h-full object-contain" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon style={{ width: effectiveSize * 0.15, height: effectiveSize * 0.15 }} className="text-gray-600" />
            </div>
          )}
        </div>
        
        {/* 文字区域 - 底部 */}
        <div className="absolute bottom-0 left-0 right-0 p-3" style={{ paddingLeft: effectiveSize * 0.04 }}>
          <h4 className={`font-bold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`} style={{ fontSize: titleSize }}>
            {product.name}
          </h4>
          {product.series_name && (
            <p style={{ color: accentColor, fontSize: subtitleSize }}>{product.series_name}</p>
          )}
          {(!isCompact || isMerged) && product.pitch_range && (
            <p className={`truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} style={{ fontSize: smallTextSize, marginTop: 2 }}>
              {product.pitch_range} • {product.process_type || 'LED'}
            </p>
          )}
        </div>
        
        <RemoveButton />
      </div>
    );
  }

  // ========== 5. 极简留白 (minimal) ==========
  if (styleType === 'minimal') {
    return (
      <div className={`w-full h-full group relative flex flex-col items-center justify-center p-4 ${showCardBackground ? 'bg-white' : ''}`}>
        {/* 图片 - 居中，较小 */}
        <div className={`flex-shrink-0 rounded-lg overflow-hidden flex items-center justify-center ${showCardBackground ? 'bg-gray-50' : ''}`}
          style={{ width: '70%', height: '50%' }}>
          {product.image_url ? (
            <img src={product.image_url} alt={product.name} className="w-full h-full object-contain p-2" />
          ) : (
            <ImageIcon style={{ width: effectiveSize * 0.15, height: effectiveSize * 0.15 }} className="text-gray-300" />
          )}
        </div>
        
        {/* 文字 - 极简 */}
        <div className="mt-3 text-center">
          <h4 className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-800'}`} style={{ fontSize: titleSize }}>
            {product.name}
          </h4>
          {(!isCompact || isMerged) && (
            <p className={`mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} style={{ fontSize: smallTextSize }}>
              {product.pitch_range || product.series_name || ''}
            </p>
          )}
        </div>
        
        <RemoveButton />
      </div>
    );
  }

  // 默认使用标准布局
  return (
    <div className="w-full h-full flex flex-col group relative">
      <div className={`flex-1 overflow-hidden flex items-center justify-center ${showCardBackground ? 'bg-gray-900' : ''}`}>
        {product.image_url ? (
          <img src={product.image_url} alt={product.name} className="w-full h-full object-contain" />
        ) : (
          <ImageIcon style={{ width: effectiveSize * 0.15, height: effectiveSize * 0.15 }} className="text-gray-600" />
        )}
      </div>
      <div className="p-2">
        <h4 className={`font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
          style={{ fontSize: titleSize }}>{product.name}</h4>
      </div>
      <RemoveButton />
    </div>
  );
}

export default CanvasGrid;
